import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Modal from '../../Modal';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';
import { SearchableSelect } from '../filters/FilterDropdowns';
import Toggle from '../../shared/Toggle';
import { PROJECT_MAIL_TYPES } from '../../../lib/projectMailTypes';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MAX_BILLING_AMOUNT  = 999999999999;
const MAX_ESTIMATED_HOURS = 100000;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EXTRA_CC = 4;
const MAX_EMAIL_LENGTH = 254; // RFC 5321 max total length for an email address

const defaultMailToggles = () => Object.fromEntries(PROJECT_MAIL_TYPES.map((t) => [t.key, true]));

/* ── Section card wrapper (mirrors CreateTaskModal's Card/CardLabel) ────────── */

function Card({ children, style }) {
  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: '13px 15px',
      background: 'var(--bg-card)',
      ...style,
    }}>
      {children}
    </div>
  );
}

function CardLabel({ icon, text, required, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
      fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1,
    }}>
      <span style={{ display: 'flex', flexShrink: 0, marginBottom: 2 }}>{icon}</span>
      <span style={{ flex: 1 }}>
        {text}
        {required && <span style={{ color: '#e05555' }}> *</span>}
      </span>
      {action}
    </div>
  );
}

const IC = {
  name: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <rect x="1.5" y="1.5" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 5h5M4 7h5M4 9h3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  building: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <rect x="2" y="1.5" width="6" height="10" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 4h2M4 6.5h2M4 9h2" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M8 6h3v5.5H8z" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  ),
  user: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="6.5" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M1.5 11.5c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  users: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <circle cx="4.5" cy="4" r="2" stroke="currentColor" strokeWidth="1.1" />
      <circle cx="9" cy="4.5" r="1.6" stroke="currentColor" strokeWidth="1.1" />
      <path d="M1 11c0-2.2 1.6-4 3.5-4s3.5 1.8 3.5 4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M7.5 7.3c1.6.2 2.8 1.8 2.8 3.7" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  calendar: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <rect x="1.5" y="2.5" width="10" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 1.5v2M9 1.5v2M1.5 5h10" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  dollar: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M6.5 1v11" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M9 3.7c0-1-1.1-1.7-2.5-1.7S4 2.7 4 3.7c0 2.3 5 1 5 3.3 0 1-1.1 1.7-2.5 1.7S4 8.3 4 7.3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  ),
  flag: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M2.5 1v11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M2.5 2h8l-2 3 2 3h-8" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  desc: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <path d="M1.5 3.5h10M1.5 6.5h7M1.5 9.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  mail: (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
      <rect x="1.5" y="2.5" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path d="M2 3.5l4.5 3.5L11 3.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

function MilestoneRow({ row, onChange, onRemove }) {
  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start',
      background: 'var(--bg-muted)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: '8px 10px',
    }}>
      <input
        type="text"
        className="form-input"
        placeholder="Milestone title *"
        value={row.title}
        onChange={(e) => onChange('title', e.target.value)}
        maxLength={200}
        style={{ margin: 0, flex: 2, minWidth: 0 }}
      />
      <input
        type="date"
        className="form-input"
        value={row.start_date}
        onChange={(e) => onChange('start_date', e.target.value)}
        style={{ margin: 0, flex: 1, minWidth: 0 }}
        title="Start date"
      />
      <input
        type="date"
        className="form-input"
        value={row.end_date}
        onChange={(e) => onChange('end_date', e.target.value)}
        style={{ margin: 0, flex: 1, minWidth: 0 }}
        title="End date"
      />
      <button
        type="button"
        onClick={onRemove}
        title="Remove milestone"
        style={{
          flexShrink: 0, width: 28, height: 34, padding: 0,
          background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)',
          borderRadius: 6, cursor: 'pointer', color: '#f87171', fontSize: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        ×
      </button>
    </div>
  );
}

export default function CreateProjectModal({ open, onClose, members, project, onSaved, roles, roleAssignments }) {
  const { appId } = useParams();
  const api       = useApi();
  const { me } = useWorkspace();

  const isEdit = !!project;

  const [name,             setName]             = useState('');
  const [clientId,         setClientId]         = useState('');
  const [clientSearch,     setClientSearch]     = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clients,          setClients]          = useState([]);
  const [clientDetails,    setClientDetails]    = useState('');
  const [billingType,      setBillingType]      = useState('fixed'); // 'fixed' | 'per_hour'
  const [totalBilling,     setTotalBilling]     = useState('');
  const [totalEstHours,    setTotalEstHours]    = useState('');
  const [ownerId,          setOwnerId]          = useState('');
  const [selectedMembers,  setSelectedMembers]  = useState([]);
  const [memberSearch,     setMemberSearch]     = useState('');
  const [startDate,        setStartDate]        = useState('');
  const [endDate,          setEndDate]          = useState('');
  const [remarks,          setRemarks]          = useState('');
  const [submitting,       setSubmitting]       = useState(false);
  const [error,            setError]            = useState(null);
  const [feedSending,      setFeedSending]      = useState(false);
  const [feedSent,         setFeedSent]         = useState(false);
  const [feedModalOpen,    setFeedModalOpen]    = useState(false);
  const [feedModalText,    setFeedModalText]    = useState('');

  // milestoneRows: { _key, id?, title, start_date, end_date, _deleted?, _dirty? }
  const [milestoneRows, setMilestoneRows] = useState([]);

  // Create-only mail settings — edits go through the project's own
  // ⚙ Mail Settings modal (ProjectMailSettingsModal) instead.
  const [mailToggles, setMailToggles] = useState(defaultMailToggles);
  const [extraCc,     setExtraCc]     = useState([]);
  const [newCcEmail,  setNewCcEmail]  = useState('');
  const [ccError,     setCcError]     = useState('');

  // Fetch contract clients for the dropdown whenever modal opens
  useEffect(() => {
    if (!open) return;
    api.listContractClients(appId, { status: 'active' })
      .then(data => setClients(Array.isArray(data) ? data : []))
      .catch(() => setClients([]));
  }, [open, appId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form when modal opens — run only when open/project identity changes,
  // not when members/api objects are re-created by the parent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setClientSearch('');
    setClientDropdownOpen(false);
    setMemberSearch('');

    if (isEdit) {
      setName(project.name || '');
      setClientId(project.client_id ? String(project.client_id) : '');
      setClientDetails(project.client_details || '');
      setOwnerId(project.owner_user_id ? String(project.owner_user_id) : '');
      setSelectedMembers(
        (project.member_user_ids || (project.members || []).map((m) => (typeof m === 'number' ? m : m.user_id)) || []).map(String)
      );
      setStartDate(project.start_date || '');
      setEndDate(project.end_date || '');
      setRemarks(project.remarks || '');
      setBillingType(project.billing_type || 'fixed');
      setTotalBilling(project.total_billing != null ? String(project.total_billing) : '');
      setTotalEstHours(project.total_estimated_hours != null ? String(project.total_estimated_hours) : '');
      // Load existing milestones for edit mode
      api.listMilestones(appId, project.id)
        .then((rows) => {
          setMilestoneRows(rows.map((r) => ({
            _key: r.id,
            id: r.id,
            title: r.title,
            start_date: r.start_date || '',
            end_date: r.end_date || '',
            _deleted: false,
            _dirty: false,
          })));
        })
        .catch(() => setMilestoneRows([]));
    } else {
      setName('');
      setClientId('');
      setClientDetails('');
      const meInMembers = members?.find((m) => m.user_id === me?.id);
      const defaultOwner = meInMembers ? String(me.id) : (members?.length ? String(members[0].user_id) : '');
      setOwnerId(defaultOwner);
      setSelectedMembers(defaultOwner ? [defaultOwner] : []);
      setStartDate('');
      setEndDate('');
      setRemarks('');
      setBillingType('fixed');
      setTotalBilling('');
      setTotalEstHours('');
      // Start with one blank milestone row so the section is immediately usable
      setMilestoneRows([{ _key: Date.now(), title: '', start_date: '', end_date: '' }]);
      setMailToggles(defaultMailToggles());
      setExtraCc([]);
      setNewCcEmail('');
      setCcError('');
    }
  // open and project.id are the only true triggers; members/me/api are read inside but stable
  }, [open, isEdit, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function matchesClientSearch(c) {
    if (!clientSearch) return true;
    const q = clientSearch.toLowerCase();
    return c.client_name.toLowerCase().includes(q)
      || (c.contact_person || '').toLowerCase().includes(q);
  }

  function handleOwnerChange(val) {
    setOwnerId(val);
    if (val && !selectedMembers.includes(val)) {
      setSelectedMembers((prev) => [...prev, val]);
    }
  }

  function toggleMember(uid) {
    const ownerStr = String(ownerId);
    if (uid === ownerStr) return;
    setSelectedMembers((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  }

  function addMilestoneRow() {
    setMilestoneRows((prev) => [
      ...prev,
      { _key: Date.now(), title: '', start_date: '', end_date: '' },
    ]);
  }

  function updateMilestoneRow(_key, field, value) {
    setMilestoneRows((prev) =>
      prev.map((r) => r._key === _key ? { ...r, [field]: value, _dirty: true } : r)
    );
  }

  function removeMilestoneRow(_key, id) {
    if (id) {
      setMilestoneRows((prev) =>
        prev.map((r) => r._key === _key ? { ...r, _deleted: true } : r)
      );
    } else {
      setMilestoneRows((prev) => prev.filter((r) => r._key !== _key));
    }
  }

  // Returns an error message, or '' when the email is OK to add.
  function extraCcError(email) {
    if (email.length > MAX_EMAIL_LENGTH) return `That email is too long (max ${MAX_EMAIL_LENGTH} characters)`;
    if (!EMAIL_RE.test(email)) return 'Enter a valid email address, e.g. name@company.com';
    if (extraCc.length >= MAX_EXTRA_CC) return `You can add at most ${MAX_EXTRA_CC} extra CC recipients`;
    if (extraCc.includes(email)) return `"${email}" already exists in the CC list`;
    // Already a recipient of every project mail — no need to CC them again.
    const client = clients.find((c) => String(c.id) === clientId);
    if (client?.email && client.email.toLowerCase() === email) {
      return `"${email}" already exists — it's the client's email`;
    }
    const member = (members || []).find((m) =>
      selectedMembers.includes(String(m.user_id)) && (m.email || '').toLowerCase() === email);
    if (member) return `"${email}" already exists — it's a project member's email`;
    return '';
  }

  function addExtraCc() {
    const email = newCcEmail.trim().toLowerCase();
    if (!email) return;
    const err = extraCcError(email);
    setCcError(err);
    if (err) return;
    setExtraCc((prev) => [...prev, email]);
    setNewCcEmail('');
  }

  function openFeedModal() {
    const selectedClient = clients.find(c => String(c.id) === clientId);
    const lines = [`📢 Project update: ${name.trim()}`];
    if (selectedClient) lines.push(`Client: ${selectedClient.client_name}`);
    if (startDate && endDate) lines.push(`Period: ${startDate} to ${endDate}`);
    if (remarks.trim()) lines.push(`Remarks: ${remarks.trim()}`);
    setFeedModalText(lines.join('\n'));
    setFeedModalOpen(true);
  }

  async function handleSendFeed(text) {
    if (!text.trim() || selectedMembers.length === 0) return;
    setFeedSending(true);
    setFeedSent(false);
    try {
      const htmlContent = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      await api.createPost(appId, {
        content:       htmlContent,
        audience_type: 'users',
        audience_ids:  selectedMembers.map(Number),
      });
      setFeedSent(true);
      setFeedModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send feed');
    } finally {
      setFeedSending(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!name.trim())  { setError('Project name is required'); return; }
    if (!clientId)     { setError('Client is required'); return; }
    if (!startDate)         { setError('Start date is required'); return; }
    if (endDate && startDate && endDate < startDate){ setError('End date must be on or after start date'); return; }
    if (selectedMembers.length === 0) { setError('Add at least one member'); return; }
    if (totalBilling !== '') {
      const v = Number(totalBilling);
      if (!Number.isFinite(v) || v < 0) { setError('Billing amount must be a non-negative number'); return; }
      if (v > MAX_BILLING_AMOUNT) { setError(`Billing amount too large (max ${MAX_BILLING_AMOUNT.toLocaleString()})`); return; }
    }
    if (billingType === 'per_hour' && totalEstHours !== '') {
      const v = Number(totalEstHours);
      if (!Number.isFinite(v) || v < 0) { setError('Estimated hours must be a non-negative number'); return; }
      if (v > MAX_ESTIMATED_HOURS) { setError(`Estimated hours too large (max ${MAX_ESTIMATED_HOURS.toLocaleString()})`); return; }
    }

    // Validate milestones
    const activeRows = milestoneRows.filter((r) => !r._deleted);
    if (activeRows.length === 0) { setError('Add at least one milestone'); return; }
    for (const row of activeRows) {
      if (!row.title.trim()) { setError('At least one milestone is required'); return; }
      if (row.start_date && !DATE_RE.test(row.start_date)) { setError('Invalid milestone start date'); return; }
      if (row.end_date && !DATE_RE.test(row.end_date)) { setError('Invalid milestone end date'); return; }
      if (row.start_date && row.end_date && row.end_date < row.start_date) {
        setError(`Milestone "${row.title}" end date must be on or after start date`); return;
      }
    }

    // A CC typed in but not yet added with "+ Add" still counts.
    let ccList = extraCc;
    const pendingCc = newCcEmail.trim().toLowerCase();
    if (!isEdit && pendingCc && !extraCc.includes(pendingCc)) {
      const ccErr = extraCcError(pendingCc);
      if (ccErr) { setCcError(ccErr); return; } // shown once, under the Extra CC input
      ccList = [...extraCc, pendingCc];
    }

    setSubmitting(true);
    try {
      const selectedClient = clients.find(c => String(c.id) === clientId);
      const body = {
        name:            name.trim(),
        client_id:       clientId ? Number(clientId) : null,
        client_name:     selectedClient?.client_name || null,
        client_details:  clientDetails.trim() || null,
        owner_user_id:   ownerId ? Number(ownerId) : null,
        start_date:      startDate || null,
        end_date:        endDate   || null,
        remarks:         remarks.trim() || null,
        member_user_ids: selectedMembers.map((uid) => Number(uid)),
        billing_type:    billingType,
        total_billing:   totalBilling !== '' ? Number(totalBilling) : null,
        total_estimated_hours: billingType === 'per_hour' && totalEstHours !== '' ? Number(totalEstHours) : null,
      };

      let projectId;
      if (isEdit) {
        await api.updateProject(appId, project.id, body);
        projectId = project.id;

        // Sync milestones for edit mode. Creates/updates run before deletions
        // so that replacing a project's only milestone in one save (delete
        // old + add new) never leaves it at zero mid-sync — the server
        // rejects deleting a project's last remaining milestone.
        for (const row of milestoneRows) {
          if (row._deleted) continue;
          if (!row.id && row.title.trim()) {
            await api.createMilestone(appId, projectId, {
              title:      row.title.trim(),
              start_date: row.start_date || null,
              end_date:   row.end_date   || null,
            });
          } else if (row.id && row._dirty) {
            await api.updateMilestone(appId, projectId, row.id, {
              title:      row.title.trim(),
              start_date: row.start_date || null,
              end_date:   row.end_date   || null,
            });
          }
        }
        for (const row of milestoneRows) {
          if (row._deleted && row.id) {
            await api.deleteMilestone(appId, projectId, row.id);
          }
        }
      } else {
        // Include milestones inline so they're created atomically with the project
        body.milestones = activeRows.map((r) => ({
          title:      r.title.trim(),
          start_date: r.start_date || null,
          end_date:   r.end_date   || null,
        }));
        Object.assign(body, mailToggles, { mail_extra_cc: ccList });
        const created = await api.createProject(appId, body);
        projectId = created.id;

        // Auto-send feed announcement to project members
        if (body.member_user_ids.length > 0) {
          const lines = [`🚀 New project: ${body.name}`];
          if (body.client_name) lines.push(`Client: ${body.client_name}`);
          if (body.start_date && body.end_date) lines.push(`Period: ${body.start_date} to ${body.end_date}`);
          if (body.remarks) lines.push(`Remarks: ${body.remarks}`);
          try {
            await api.createPost(appId, {
              content:       lines.join('<br>'),
              audience_type: 'users',
              audience_ids:  body.member_user_ids,
            });
          } catch (_) { /* feed failure should not block project creation */ }
        }
      }

      await onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save project');
      setSubmitting(false);
    }
  };

  const visibleRows = milestoneRows.filter((r) => !r._deleted);

  const filteredMembers = (members || []).filter((m) => {
    if (!memberSearch.trim()) return true;
    const q = memberSearch.toLowerCase();
    const label = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
    return label.toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q);
  });

  return (
    <>
    <Modal open={open} title={isEdit ? 'Edit project' : 'New project'} onClose={submitting ? undefined : onClose} size="2xl" disableBackdropClose>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

        <p style={{ margin: '0 0 6px', fontSize: 12.5, color: 'var(--text-muted)' }}>
          Set up a client project, its team, milestones and timeline
        </p>

        {/* Row 1 — Project Name + Client Name */}
        <Card style={{ padding: '11px 15px' }}>
          <div>
            <CardLabel icon={IC.name} text="Project Name" required />
            <input
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 200))}
              placeholder="e.g. Website Redesign"
              autoFocus
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
              {name.length}/200
            </div>
          </div>
          <div style={{ height: 1, background: 'var(--border)', margin: '10px 0' }} />
          <div style={{ position: 'relative' }}>
            <CardLabel icon={IC.building} text="Client" required />
            <input
              type="text"
              className="form-input"
              placeholder="Search client…"
              value={clientSearch}
              onChange={(e) => { setClientSearch(e.target.value); setClientId(''); }}
              onFocus={() => setClientDropdownOpen(true)}
              onBlur={() => setTimeout(() => setClientDropdownOpen(false), 150)}
              maxLength={100}
              style={{ width: '100%', marginBottom: 4 }}
            />
            {clientDropdownOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                border: '1px solid var(--border)', borderRadius: 8,
                maxHeight: 200, overflowY: 'auto',
                background: 'var(--bg-card)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
              }}>
                {clients
                  .filter(matchesClientSearch)
                  .map((c) => (
                    <div
                      key={c.id}
                      onMouseDown={() => { setClientId(String(c.id)); setClientSearch(c.client_name); setClientDropdownOpen(false); }}
                      style={{
                        padding: '8px 14px', cursor: 'pointer', fontSize: 13,
                        background: clientId === String(c.id) ? 'var(--bg-muted)' : 'transparent',
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      {c.client_name}
                      {c.client_code && <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 11 }}>{c.client_code}</span>}
                      {c.contact_person && <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: 11 }}>· {c.contact_person}</span>}
                    </div>
                  ))}
                {clients.filter(matchesClientSearch).length === 0 && (
                  <div style={{ padding: '8px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No clients found</div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Row 2 — Owner | Start date | End date */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.user} text="Project Owner" />
            <SearchableSelect
              value={ownerId}
              onChange={(v) => handleOwnerChange(v)}
              options={[
                { value: '', label: '— Select owner —' },
                ...(members || []).map((m) => ({
                  value: m.user_id,
                  label: `${[m.first_name, m.last_name].filter(Boolean).join(' ') || m.email}${me && m.user_id === me.id ? ' (you)' : ''}`,
                })),
              ]}
              showAll={false}
              btnClass="form-select-btn"
              matchTriggerWidth
            />
          </Card>
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.calendar} text="Start Date" required />
            <input
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </Card>
          <Card style={{ padding: '9px 13px' }}>
            <CardLabel icon={IC.calendar} text="End Date" />
            <input
              type="date"
              className="form-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </Card>
        </div>

        {/* Row 3 — Client details */}
        <Card style={{ padding: '11px 15px' }}>
          <CardLabel icon={IC.desc} text="Client Details" />
          <textarea
            className="form-input"
            rows={2}
            value={clientDetails}
            onChange={(e) => setClientDetails(e.target.value)}
            maxLength={2000}
            placeholder="Contact info, address, notes…"
            style={{ resize: 'none', width: '100%' }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
            {clientDetails.length}/2000
          </div>
        </Card>

        {/* Row 4 — Members multi-select */}
        <Card style={{ padding: '11px 15px' }}>
          <CardLabel
            icon={IC.users}
            text="Members"
            required
            action={
              selectedMembers.length > 0 && (
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
                  {selectedMembers.length} selected
                </span>
              )
            }
          />
          <input
            type="text"
            className="form-input"
            placeholder="Search members…"
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            maxLength={100}
            style={{ width: '100%', marginBottom: 6 }}
          />
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            maxHeight: 180,
            overflowY: 'auto',
            background: 'var(--bg-muted)',
          }}>
            {members?.length === 0 && (
              <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No members found</div>
            )}
            {members?.length > 0 && filteredMembers.length === 0 && (
              <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No matches</div>
            )}
            {filteredMembers.map((m) => {
              const uid      = String(m.user_id);
              const checked  = selectedMembers.includes(uid);
              const isOwner  = uid === String(ownerId);
              const label    = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
              return (
                <label
                  key={m.user_id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 14px',
                    cursor: isOwner ? 'default' : 'pointer',
                    borderBottom: '1px solid var(--border)',
                    background: checked ? 'var(--bg-card)' : 'transparent',
                    transition: 'background .15s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isOwner}
                    onChange={() => toggleMember(uid)}
                    style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1 }}>{label}</span>
                  {isOwner && (
                    <span style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'var(--bg-card)' }}>
                      Owner
                    </span>
                  )}
                  {me && m.user_id === me.id && !isOwner && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>you</span>
                  )}
                </label>
              );
            })}
          </div>
        </Card>

        {/* Row 5 — Billing & cost estimate */}
        <Card style={{ padding: '11px 15px' }}>
          <CardLabel text="Billing &amp; Cost Estimate" />

          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5,
              color: 'var(--text-primary)', cursor: 'pointer', padding: '4px 10px',
              borderRadius: 99, border: `1px solid ${billingType === 'fixed' ? 'var(--accent)' : 'var(--border)'}`,
              background: billingType === 'fixed' ? 'var(--bg-muted)' : 'transparent',
            }}>
              <input type="radio" name="billingType" checked={billingType === 'fixed'} onChange={() => setBillingType('fixed')} style={{ margin: 0 }} />
              Fixed Rate
            </label>
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5,
              color: 'var(--text-primary)', cursor: 'pointer', padding: '4px 10px',
              borderRadius: 99, border: `1px solid ${billingType === 'per_hour' ? 'var(--accent)' : 'var(--border)'}`,
              background: billingType === 'per_hour' ? 'var(--bg-muted)' : 'transparent',
            }}>
              <input type="radio" name="billingType" checked={billingType === 'per_hour'} onChange={() => setBillingType('per_hour')} style={{ margin: 0 }} />
              Per Hour
            </label>
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>
                {billingType === 'per_hour' ? 'Rate Per Hour' : 'Total Amount'}
              </div>
              <input
                type="number"
                min="0"
                max={MAX_BILLING_AMOUNT}
                step="0.01"
                className="form-input"
                value={totalBilling}
                onChange={(e) => setTotalBilling(e.target.value)}
                placeholder="0.00"
                style={{ width: 180 }}
              />
            </div>
            {billingType === 'per_hour' && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>Total Estimated Hours</div>
                <input
                  type="number"
                  min="0"
                  max={MAX_ESTIMATED_HOURS}
                  step="0.01"
                  className="form-input"
                  value={totalEstHours}
                  onChange={(e) => setTotalEstHours(e.target.value)}
                  placeholder="0"
                  style={{ width: 180 }}
                />
              </div>
            )}
          </div>
        </Card>

        {/* Row 6 — Milestones */}
        <Card style={{ padding: '11px 15px' }}>
          <CardLabel
            icon={IC.flag}
            text="Milestones"
            required
            action={
              <button
                type="button"
                onClick={addMilestoneRow}
                style={{
                  fontSize: 12, fontWeight: 600, padding: '3px 12px',
                  background: 'var(--bg-muted)', border: '1px solid var(--border)',
                  borderRadius: 6, color: 'var(--accent)', cursor: 'pointer',
                }}
              >
                + Add milestone
              </button>
            }
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {visibleRows.map((row) => (
              <MilestoneRow
                key={row._key}
                row={row}
                onChange={(field, val) => updateMilestoneRow(row._key, field, val)}
                onRemove={() => removeMilestoneRow(row._key, row.id)}
              />
            ))}
          </div>
        </Card>

        {/* Row 7 — Remarks */}
        <Card style={{ padding: '11px 15px' }}>
          <CardLabel icon={IC.desc} text="Remarks" />
          <textarea
            className="form-input"
            rows={2}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            maxLength={2000}
            placeholder="Any additional notes…"
            style={{ resize: 'none', width: '100%' }}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
            {remarks.length}/2000
          </div>
        </Card>

        {/* Row 8 — Mail settings (create only; edits use the ⚙ Mail Settings modal) */}
        {!isEdit && (
          <Card style={{ padding: '11px 15px' }}>
            <CardLabel icon={IC.mail} text="Mail Settings" />
            <p style={{ margin: '0 0 10px', fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Emails this project sends to the client, with the app owner, members and extra CCs copied.
              A type turned off workspace-wide in Other Settings &gt; Mail Control won&apos;t send even if on here.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '8px 20px' }}>
              {PROJECT_MAIL_TYPES.map((t) => (
                <div key={t.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{t.label}</span>
                  <Toggle
                    checked={mailToggles[t.key]}
                    onChange={(v) => setMailToggles((prev) => ({ ...prev, [t.key]: v }))}
                  />
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: 'var(--border)', margin: '12px 0 10px' }} />
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
              Extra CC <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(up to {MAX_EXTRA_CC})</span>
            </div>
            {extraCc.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {extraCc.map((email) => (
                  <span key={email} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '3px 8px', background: 'var(--bg-muted)',
                    border: '1px solid var(--border)', borderRadius: 99, fontSize: 12,
                    color: 'var(--text-primary)',
                  }}>
                    {email}
                    <button
                      type="button"
                      onClick={() => setExtraCc((prev) => prev.filter((e) => e !== email))}
                      title="Remove"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, padding: 0, lineHeight: 1 }}
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            {extraCc.length < MAX_EXTRA_CC && (
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  className="form-input"
                  placeholder="name@company.com"
                  value={newCcEmail}
                  onChange={(e) => { setNewCcEmail(e.target.value); setCcError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addExtraCc(); } }}
                  maxLength={MAX_EMAIL_LENGTH}
                  style={{ margin: 0, flex: 1, fontSize: 12.5 }}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={addExtraCc}
                  disabled={!newCcEmail.trim()}
                  style={{ padding: '4px 14px', fontSize: 12.5, flexShrink: 0 }}
                >
                  + Add
                </button>
              </div>
            )}
            {ccError && <div className="form-error" style={{ marginTop: 8 }}>{ccError}</div>}
          </Card>
        )}

        {error && <div className="form-error">{error}</div>}

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {isEdit && selectedMembers.length > 0 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={openFeedModal}
              disabled={submitting}
              style={{ color: feedSent ? 'var(--pos)' : undefined }}
            >
              {feedSent ? '✓ Feed Sent' : '📢 Send Feed'}
            </button>
          )}
          <button type="submit" className="btn-primary" disabled={submitting} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.3" />
              <path d="M7 4.5v5M4.5 7h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {submitting ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save Changes' : 'Create Project')}
          </button>
        </div>
      </form>
    </Modal>

    {/* Feed compose modal — stacks on top of edit modal */}
    <Modal
      open={feedModalOpen}
      title="Send Feed Update"
      onClose={() => { if (!feedSending) setFeedModalOpen(false); }}
      size="sm"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 13, color: 'var(--fg-mute)' }}>
          This message will be sent to all project members.
        </div>
        <div>
          <textarea
            className="form-input"
            rows={6}
            value={feedModalText}
            onChange={(e) => setFeedModalText(e.target.value)}
            maxLength={2000}
            placeholder="Write your update message…"
            style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, width: '100%' }}
            autoFocus
          />
          <div style={{ fontSize: 11, color: 'var(--fg-mute)', textAlign: 'right', marginTop: 2 }}>
            {feedModalText.length}/2000
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => setFeedModalOpen(false)}
            disabled={feedSending}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={() => handleSendFeed(feedModalText)}
            disabled={feedSending || !feedModalText.trim()}
          >
            {feedSending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </Modal>
    </>
  );
}
